import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Users, TrendingUp, Crown } from "lucide-react";
import { REGION_IMAGES } from "@/lib/regions";

const STATS = [
  { value: "80%", label: "of business comes from word-of-mouth referrals" },
  { value: "84%", label: "of B2B customers start their journey with a referral" },
  { value: "2.68×", label: "greater lifetime value from referred customers" },
];

const TIERS = [
  {
    name: "Ignite",
    icon: Sparkles,
    tagline: "Build the Business.",
    desc:
      "For founders ready to plug in, learn the playbook and start building real referral relationships.",
    perks: [
      "First 90-Day Momentum Plan + Member Training",
      "Monthly Maximiser events with guest speakers",
      "Fortnightly in-person growth meetings",
      "D32 App + member directory access",
      "IGNITE Growth Guarantee*",
    ],
    accent: "text-gold",
    badge: "Entry",
  },
  {
    name: "Impact",
    icon: TrendingUp,
    tagline: "Grow the Business.",
    desc:
      "For owners who've found product-market fit and want serious referral velocity, deeper learning and a Mastermind seat.",
    perks: [
      "Everything in Ignite",
      "Ladies / Mens / Sundowner curated events",
      "Online Personal Leadership Workshops",
      "Mastermind Forum (one per profession)",
      "Priority matching with high-value referrers",
    ],
    accent: "text-gold",
    badge: "Most Popular",
    featured: true,
  },
  {
    name: "Influence",
    icon: Crown,
    tagline: "Lead the Business.",
    desc:
      "Invite-only mastermind for high-revenue business owners who want to skip years of trial and error and grow faster, together.",
    perks: [
      "Private peer mastermind (₹8 Cr+ owners)",
      "Curated 1:1 introductions across India",
      "Strategy sessions with national speakers",
      "Pan-India network access",
      "Founder retreats & off-sites",
    ],
    accent: "text-gold",
    badge: "Invite-only",
  },
];

const BENEFITS = [
  {
    title: "Not just networking, a growth system",
    body:
      "We don't hand you a name tag and wish you luck. We teach you how to give and get referrals, run intentional 1:1s, and build a referral economy around your business.",
  },
  {
    title: "Built for the Indian SME reality",
    body:
      "Chandigarh Tricity, Ahmedabad, Delhi-NCR and the cities to follow, D32 India is engineered for Indian founders, manufacturers, services and professionals who want a serious peer table.",
  },
  {
    title: "Real people. Real businesses. Real ROI.",
    body:
      "From CAs and architects to manufacturers and marketers, our members aren't card-shovers. They're operators who show up, refer freely and grow together.",
  },
  {
    title: "A pathway, not a pitch deck",
    body:
      "Ignite → Impact → Influence. Three tiers designed to take you from your first ₹1 Cr to your first ₹100 Cr, with the right room at every stage.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "It's the only room in Chandigarh where every introduction feels intentional. Six months in, my referral pipeline doubled.",
    name: "Priya Sharma",
    role: "Founder, Lumen Digital",
  },
  {
    quote:
      "I joined for the leads. I stayed for the cheer squad. D32 is what entrepreneurship in India was missing.",
    name: "Hardik Patel",
    role: "MD, Patel Polymers · Ahmedabad",
  },
  {
    quote:
      "More than a network, it's a board of advisors that meets every two weeks and never sends an invoice.",
    name: "Arjun Mehta",
    role: "Partner, Northstar Legal Advisors",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-navy text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy-deep" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />

        <div className="container-narrow relative pt-32 pb-32 lg:pt-44 lg:pb-40">
          <div className="overline text-gold mb-6">The Power of Connection · India</div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-display font-semibold leading-[1.05] max-w-4xl">
            Business is easier when you're in the{" "}
            <span className="text-gold italic">right room</span>, with the right
            people.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-gray-300 leading-relaxed">
            D32 India is a business growth network for ambitious founders and
            SME owners. Get leads, learn the playbook, and build the
            relationships that actually move your business, together.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/visit-a-circle"
              data-testid="hero-cta-visit"
              className="inline-flex items-center gap-2 bg-gold text-white font-semibold px-7 py-4 rounded-full hover:bg-[#5fc1f1] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)]"
            >
              Visit a Circle <ArrowRight size={18} />
            </Link>
            <Link
              to="/franchise"
              data-testid="hero-cta-franchise"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 rounded-full hover:bg-white hover:text-navy transition-all duration-300"
            >
              Become a Franchise Partner
            </Link>
          </div>

          <div className="mt-20 grid grid-cols-3 gap-6 max-w-3xl">
            {[
              ["IGNITE", "Build the Business"],
              ["IMPACT", "Grow the Business"],
              ["INFLUENCE", "Lead the Business"],
            ].map(([k, v]) => (
              <div key={k} className="border-t border-white/15 pt-4">
                <div className="text-gold font-display text-xl sm:text-2xl">
                  {k}
                </div>
                <div className="text-xs sm:text-sm text-gray-400 mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-cream py-20 lg:py-28">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            <div>
              <div className="overline text-navy/60 mb-4">Why referrals win</div>
              <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">
                The data is loud. Referral marketing works.
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Source: Harvard Business Review. Your network, and the
                relationships inside it, are your most defensible competitive
                advantage.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-3 gap-8">
              {STATS.map((s) => (
                <div
                  key={s.value}
                  data-testid={`stat-${s.value}`}
                  className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-5xl font-display font-semibold text-navy">
                    {s.value}
                  </div>
                  <div className="mt-3 text-sm text-gray-600 leading-relaxed">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="bg-white py-24 lg:py-32" id="tiers">
        <div className="container-narrow">
          <div className="max-w-2xl mb-16">
            <div className="overline text-gold mb-4">The Pathway to Growth</div>
            <h2 className="text-4xl sm:text-5xl font-display font-semibold text-navy leading-tight">
              Three tiers. One growth journey.
            </h2>
            <p className="mt-5 text-gray-600 text-lg">
              Whether you're scaling past your first ₹1 Cr or pushing through
              ₹100 Cr, there's a D32 India room built for where you are now.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {TIERS.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.name}
                  data-testid={`tier-card-${tier.name.toLowerCase()}`}
                  className={`relative p-8 lg:p-10 rounded-2xl border transition-all duration-300 ${
                    tier.featured
                      ? "bg-navy text-white border-navy lg:scale-[1.02] shadow-2xl"
                      : "bg-white border-gray-200 hover:border-navy/40 hover:-translate-y-1"
                  }`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        tier.featured ? "bg-gold text-white" : "bg-navy text-gold"
                      }`}
                    >
                      <Icon size={22} />
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full ${
                        tier.featured
                          ? "bg-gold text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {tier.badge}
                    </span>
                  </div>
                  <h3
                    className={`text-3xl font-display font-semibold ${
                      tier.featured ? "text-white" : "text-navy"
                    }`}
                  >
                    {tier.name}
                  </h3>
                  <div
                    className={`mt-1 text-sm italic ${
                      tier.featured ? "text-gold" : "text-gray-500"
                    }`}
                  >
                    {tier.tagline}
                  </div>
                  <p
                    className={`mt-5 text-sm leading-relaxed ${
                      tier.featured ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {tier.desc}
                  </p>
                  <ul className="mt-7 space-y-3">
                    {tier.perks.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm">
                        <Check
                          size={16}
                          className={`mt-0.5 flex-shrink-0 ${
                            tier.featured ? "text-gold" : "text-navy"
                          }`}
                        />
                        <span
                          className={
                            tier.featured ? "text-gray-200" : "text-gray-700"
                          }
                        >
                          {p}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/visit-a-circle"
                    className={`mt-8 inline-flex items-center gap-1 text-sm font-semibold ${
                      tier.featured ? "text-gold" : "text-navy"
                    } hover:gap-2 transition-all`}
                  >
                    Visit a meeting <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-xs text-gray-500 italic">
            *Eligible Ignite members who actively participate and don't generate
            business equal to 2× their membership investment may qualify for a
            complimentary renewal period. Full conditions apply.
          </p>
        </div>
      </section>

      {/* BENEFITS - asymmetric */}
      <section className="bg-cream py-24 lg:py-32">
        <div className="container-narrow grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-gold mb-4">Why D32 India</div>
            <h2 className="text-4xl sm:text-5xl font-display font-semibold text-navy leading-tight">
              We don't run a networking club. We run a growth engine.
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              India has no shortage of meetups. What we needed was a serious,
              structured, give-first network engineered around how Indian
              businesses actually grow. So we built one.
            </p>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-navy font-semibold border-b-2 border-gold pb-1 hover:gap-3 transition-all"
            >
              Read our story <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {BENEFITS.map((b, i) => (
              <div
                key={b.title}
                className={`bg-white rounded-2xl p-7 border border-gray-200 ${
                  i % 2 === 1 ? "sm:translate-y-8" : ""
                }`}
              >
                <Users size={18} className="text-gold mb-3" />
                <h3 className="font-display text-xl text-navy">{b.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="container-narrow relative">
          <div className="overline text-gold mb-4">Voices from the room</div>
          <h2 className="text-4xl sm:text-5xl font-display font-semibold leading-tight max-w-2xl">
            Built by members. Backed by results.
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                className="border border-white/15 rounded-2xl p-8 hover:border-gold/60 transition-colors bg-white/[0.02]"
              >
                <div className="text-gold text-3xl font-display leading-none">"</div>
                <p className="mt-2 text-gray-200 leading-relaxed">{t.quote}</p>
                <div className="mt-6 text-sm">
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-gray-400">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGIONS PREVIEW */}
      <section className="bg-white pt-24 lg:pt-28 pb-8 lg:pb-10">
        <div className="container-narrow">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="overline text-gold mb-3">Where we're live</div>
              <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">
                Three regions live. More opening every quarter.
              </h2>
            </div>
            <Link
              to="/find-a-region"
              className="inline-flex items-center gap-2 text-navy font-semibold border-b-2 border-gold pb-1 hover:gap-3 transition-all"
            >
              Find a D32 Region <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                city: "Chandigarh Tricity",
                slug: "chandigarh-tricity",
                state: "Chandigarh · Panchkula · Mohali",
                img: REGION_IMAGES["chandigarh-tricity"],
                slot: "Fortnightly · Wednesday morning",
              },
              {
                city: "Ahmedabad",
                slug: "ahmedabad",
                state: "Gujarat",
                img: REGION_IMAGES.ahmedabad,
                slot: "Ignite: alt. Thursdays · Impact: 1st Fri + 3rd Sat",
              },
              {
                city: "Delhi-NCR",
                slug: "delhi-ncr",
                state: "Delhi · Gurugram · Noida · Faridabad · Ghaziabad",
                img: REGION_IMAGES["delhi-ncr"],
                slot: "Schedule coming soon",
              },
            ].map((r) => (
              <Link
                key={r.city}
                to={`/find-a-region/${r.slug}`}
                data-testid={`home-region-${r.slug}`}
                className="group relative overflow-hidden rounded-2xl aspect-[16/9] md:aspect-[4/5] lg:aspect-[4/3] border border-gray-200 hover:border-gold/60 transition"
              >
                <img src={r.img} alt={r.city} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/75 to-navy/20" />
                <span className="absolute top-4 left-4 text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full bg-gold text-white shadow">
                  Live
                </span>
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="font-display text-3xl">{r.city}</div>
                  <div className="text-white/80 text-sm mt-1">{r.state}</div>
                  <div className="text-white/70 text-xs mt-2">{r.slot}</div>
                  <div className="text-white text-sm font-semibold mt-3 underline decoration-gold underline-offset-4">Meet the owner →</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-sm text-gray-500 text-center">
            Coming soon: Mumbai · Bengaluru · Pune · Hyderabad · Chennai · Kolkata · Jaipur, and more.
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-white pt-8 lg:pt-10 pb-24 lg:pb-28">
        <div className="container-narrow">
          <div className="bg-gradient-to-br from-cream to-white border border-gold/30 rounded-3xl p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <div className="overline text-gold mb-3">Ready to step in?</div>
              <h3 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">
                Come to one circle meeting. See it for yourself.
              </h3>
              <p className="mt-4 text-gray-600">
                We'll save you a seat at the next Chandigarh Tricity, Ahmedabad or Delhi-NCR meeting. No pressure, no pitch, just the room.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <Link
                to="/visit-a-circle"
                data-testid="cta-strip-visit"
                className="inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold px-7 py-4 rounded-full whitespace-nowrap hover:-translate-y-0.5 transition-all"
              >
                Visit a Circle <ArrowRight size={18} />
              </Link>
              <Link
                to="/find-a-region"
                data-testid="cta-strip-circles"
                className="inline-flex items-center justify-center gap-2 border border-navy text-navy font-semibold px-7 py-4 rounded-full whitespace-nowrap hover:bg-navy hover:text-white transition-all"
              >
                Find a Region
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
